import {
  Box,
  Button,
  Modal,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useTheme } from "@mui/material";
import { useState, useEffect } from "react";
import { iepaProd } from "../../constants/global";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import RibbonNavbar from "../../components/RibbonNavbar";
import SearchInput from "../../components/SearchInput";

const CustomerMaster = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [selectedRows, setSelectedRows] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [priceRule, setPriceRule] = useState("default");
  const [paymentTerm, setPaymentTerm] = useState([]);
  const [paymentTermSelected, setPaymentTermSelected] = useState(null);
  const [creditCheck, setCreditCheck] = useState(false);
  const [creditAmount, setCreditAmount] = useState("");
  const [override, setOverride] = useState(false);
  const [overrideCount, setOverrideCount] = useState("");
  const [filterText, setFilterText] = useState("");
  const [customers, setCustomers] = useState([]);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const handleEdit = () => setEditMode(true);
  const handleView = () => setEditMode(false);
  /* ================= FETCH DATA ================= */

  const fetchCustomers = async () => {
    fetch(`${iepaProd.iepaUserSrvAPI}/customer/all-customers/org/1`)
      .then((res) => res.json())
      .then(setCustomers)
      .catch((err) => setError(err.message));
  };

  const fetchPaymentTerms = async () => {
    fetch(`${iepaProd.iepaOrderMgmtAPI}/orm/payment_terms/org/1`)
      .then((res) => res.json())
      .then(setPaymentTerm)
      .catch((err) => setError(err.message));
  };

  useEffect(() => {
    fetchCustomers();
    fetchPaymentTerms();
  }, []);

  /* ================= FILTER ================= */
const filteredRows = customers.filter(
  (row) =>
    String(row?.id).toLowerCase().includes(filterText.toLowerCase()) ||
    row?.name?.toLowerCase().includes(filterText.toLowerCase()) ||
    row?.ownerName?.toLowerCase().includes(filterText.toLowerCase()) ||
    row?.contact?.email?.toLowerCase().includes(filterText.toLowerCase()) ||
    row?.contact?.phone?.toLowerCase().includes(filterText.toLowerCase()) ||
    row?.route?.toLowerCase().includes(filterText.toLowerCase())
);


  /* ================= GRID COLUMNS ================= */

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 80 },
    { field: "name", headerName: "Company Name", flex: 1, minWidth: 150 },
    { field: "ownerName", headerName: "Owner Name", flex: 1, minWidth: 150 },
    { field: "type", headerName: "Customer Type", flex: 1, minWidth: 150 },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 180,
      valueGetter: (_, row) => row.contact?.email,
    },
    {
      field: "phone",
      headerName: "Phone",
      flex: 1,
      minWidth: 150,
      valueGetter: (_, row) =>
        row.contact?.phone || row.contact?.mobile,
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
      minWidth: 200,
      valueGetter: (_, row) =>
        row.defaultLocation?.address?.address_1,
    },
    { field: "route", headerName: "Route", flex: 1, minWidth: 120 },
  ];

  /* ================= ACTIONS ================= */

  const handleDelete = () => {
    if (!selectedRows.length) return;
    fetch(`${iepaProd.iepaUserSrvAPI}/customer/delete`, {
      method: "DELETE",
    }).then(() => {
      fetchCustomers();
      setSelectedRows([]);
    });
  };

  const handleSave = () => {
    fetch(`${iepaProd.iepaUserSrvAPI}/customer/save/profile/org/1`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        customerId: selectedRows[0],
        paymentTermsId: paymentTermSelected,
        creditCheck,
        creditLimit: creditAmount,
        overrideCheckCount: overrideCount,
        createdBy: 1,
      }),
    }).then(() => setOpenModal(false));
  };

  /* ================= RENDER ================= */

  return (
    <Box m={2}>
      {/* HEADER */}
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        gap={2}
        justifyContent="space-between"
        alignItems={isMobile ? "stretch" : "center"}
      >
        <Header title="Customer Master" />

        <SearchInput
          filterText={filterText}
          setFilterText={setFilterText}
          placeholder="Search customers"
        />

        <RibbonNavbar
          selectedRows={selectedRows}
          onAdd={() => navigate("../CreateCustomer1")}
          onEdit={() =>
            navigate("/ViewCustomer", {
              state: { selectedRows, customers, isEditMode: true },
            })
          }
          onView={() =>
            navigate("/ViewCustomer", {
              state: { selectedRows, customers, isEditMode: false },
            })
          }
          onDelete={handleDelete}
          onManageProfile={() => setOpenModal(true)}
        />
      </Box>

      {/* GRID */}
      <Box
        mt={2}
        height={isMobile ? "65vh" : "75vh"}
        sx={{
          overflowX: "auto",
          "& .MuiDataGrid-root": { minWidth: 900, border: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
        }}
      >
        <DataGrid
          rows={filteredRows}
          columns={columns}
          checkboxSelection
          onRowSelectionModelChange={setSelectedRows}
        />
      </Box>

      {/* MODAL */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "95%" : 420,
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "background.paper",
            p: 3,
            borderRadius: 2,
          }}
        >
          <IconButton
            onClick={() => setOpenModal(false)}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>

          <h3>Manage Profile</h3>

          <Box display="flex" flexDirection="column" gap={2}>
            <TextField select label="Price Rule" value={priceRule} />
            <TextField
              select
              label="Payment Term"
              value={paymentTermSelected}
              onChange={(e) => setPaymentTermSelected(e.target.value)}
            >
              {paymentTerm.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.description}
                </option>
              ))}
            </TextField>

            <FormControlLabel
              control={
                <Switch
                  checked={creditCheck}
                  onChange={() => setCreditCheck(!creditCheck)}
                />
              }
              label="Credit Check"
            />

            {creditCheck && (
              <TextField
                type="number"
                label="Credit Amount"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
              />
            )}

            <Button
              variant="contained"
              sx={{
                backgroundColor: colors.blueAccent[700],
                fontWeight: "bold",
              }}
              onClick={handleSave}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default CustomerMaster;
